import get from 'lodash/get';
import axiosConfig from '../../../redux/Project/axiosConfig';
import { ensureAuditSession } from '../../audit/auditSession';
import { Notification as customNotification } from '../../../components/ui/custom-notification';
import type {
  AuditTrailListData,
  FetchAuditTrailParams,
  FetchAuditTrailResult,
} from '../components/dashboard/auditTrail/auditTrailTypes';

function buildQueryString(params: FetchAuditTrailParams): string {
  const qs = new URLSearchParams();
  if (params.project_id != null && Number.isFinite(params.project_id)) {
    qs.set('project_id', String(params.project_id));
  }
  qs.set('limit', String(params.limit ?? 50));
  qs.set('offset', String(params.offset ?? 0));

  if (params.module) qs.set('module', params.module);
  if (params.event_type) qs.set('event_type', params.event_type);
  if (params.severity) qs.set('severity', params.severity);
  if (params.session_id) qs.set('session_id', params.session_id);
  if (params.actor_id) qs.set('actor_id', params.actor_id);
  if (params.search) qs.set('search', params.search);
  if (params.created_from) qs.set('created_from', params.created_from);
  if (params.created_to) qs.set('created_to', params.created_to);
  if (params.entity_type) qs.set('entity_type', params.entity_type);
  if (params.entity_id) qs.set('entity_id', params.entity_id);
  if (params.sortBy) qs.set('sortBy', params.sortBy);
  if (params.sortOrder) qs.set('sortOrder', params.sortOrder);

  return qs.toString();
}

function parseListData(raw: unknown, fallbackLimit: number, fallbackOffset: number): AuditTrailListData {
  const data = raw as Record<string, unknown>;
  const itemsRaw = Array.isArray(data?.audit_trail)
    ? data.audit_trail
    : Array.isArray(data?.items)
      ? data.items
      : [];
  const items = itemsRaw as AuditTrailListData['items'];
  return {
    items,
    total: typeof data?.total === 'number' ? data.total : items.length,
    limit: typeof data?.limit === 'number' ? data.limit : fallbackLimit,
    offset: typeof data?.offset === 'number' ? data.offset : fallbackOffset,
  };
}

export async function fetchAuditTrail(
  params: FetchAuditTrailParams
): Promise<FetchAuditTrailResult> {
  const { signal, ...queryParams } = params;
  const url = `/auditTrail/getAuditTrailData?${buildQueryString(queryParams)}`;

  try {
    const res = await axiosConfig.get(url, { signal });

    if (res.status === 403) {
      customNotification.error(
        'Permission denied',
        'You do not have permission to view the audit trail.'
      );
      return { ok: false, error: 'Permission denied', statusCode: 403 };
    }

    if (res.status === 200) {
      const payload = get(res, ['data', 'data'], null);
      if (payload == null) {
        return {
          ok: true,
          data: { items: [], total: 0, limit: queryParams.limit ?? 50, offset: queryParams.offset ?? 0 },
        };
      }
      return { ok: true, data: parseListData(payload, queryParams.limit ?? 50, queryParams.offset ?? 0) };
    }

    return { ok: false, error: 'Unexpected response from audit trail API', statusCode: res.status };
  } catch (error: unknown) {
    if (signal?.aborted) {
      return { ok: false, error: 'Request cancelled' };
    }

    const err = error as {
      code?: string;
      message?: string;
      response?: { status?: number; data?: { message?: string } };
    };

    const status = err.response?.status;
    if (status === 404 || status === 501) {
      return {
        ok: false,
        error: 'Audit trail API is not available yet.',
        statusCode: status,
        unavailable: true,
      };
    }

    if (err.code === 'ERR_CANCELED') {
      return { ok: false, error: 'Request cancelled' };
    }

    if (status === 401) {
      customNotification.error('Authentication error', 'Your session may have expired.');
      return { ok: false, error: 'Unauthorized', statusCode: 401 };
    }

    if (status === 403) {
      customNotification.error('Permission denied', 'You cannot view the audit trail.');
      return { ok: false, error: 'Permission denied', statusCode: 403 };
    }

    if (status != null && status >= 500) {
      return {
        ok: false,
        error: 'Server error while loading audit trail. Please try again.',
        statusCode: status,
      };
    }

    if (err.code === 'NETWORK_ERROR' || !err.response) {
      return {
        ok: false,
        error: 'Unable to connect. Audit trail API may not be available yet.',
        unavailable: true,
      };
    }

    const message =
      err.response?.data?.message || err.message || 'Failed to load audit trail';
    return { ok: false, error: message, statusCode: status };
  }
}

export interface CreateAuditTrailRecordParams {
  eventType: string;
  module: string;
  entityType: string;
  entityId: string;
  actionDetails: Record<string, unknown>;
  projectId?: number | null;
  projectName?: string | null;
  entityName?: string | null;
  entityEmail?: string | null;
  severity?: 'INFO' | 'WARN' | 'ERR';
  summary?: string | null;
  displayTitle?: string | null;
  weldId?: string | null;
  pipeSizeId?: number | null;
}

/** Awaitable client audit log; never throws — failures are logged to console. */
export async function logClientAuditEventAsync(
  params: CreateAuditTrailRecordParams
): Promise<void> {
  const sessionId = ensureAuditSession();
  try {
    await axiosConfig.post('/auditTrail/createAuditTrailRecord', {
      ...params,
      sessionId,
    });
  } catch (error) {
    console.warn('Failed to write audit trail record', error);
  }
}

/** Fire-and-forget client audit log; never blocks UI on failure */
export function logClientAuditEvent(params: CreateAuditTrailRecordParams): void {
  void logClientAuditEventAsync(params);
}

/** Log API access denied (403) on mutating requests — fire-and-forget. */
export function logAccessDeniedAudit(endpoint: string, method: string): void {
  const path = endpoint || 'unknown';
  logClientAuditEvent({
    eventType: 'ACCESS_DENIED',
    module: 'SECURITY',
    entityType: 'API',
    entityId: path,
    severity: 'WARN',
    summary: `Access denied: ${method} ${path}.`,
    displayTitle: path,
    actionDetails: {
      endpoint: path,
      method,
      status: 403,
    },
  });
}
